/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'

import { EuiButton, EuiFlexGroup, EuiFlexItem } from '@elastic/eui'

import { useSolutionOptions } from '@modules/solution-selector/hooks'
import SelectSolutionCardV4 from '@modules/solution-selector/SelectSolutionCard'
import type { SolutionType } from '@modules/solution-selector/types'
import SelectSolutionCardV6 from '@modules/solution-selector/SelectSolutionCardV6'
import { useSolutionOptionsV6 } from '@modules/solution-selector/hooksV6'
import { FormattedMessage } from '@modules/i18n'
import TitleAndDescription from '@modules/deployment-creation-wizard/TitleAndDescription'

import type { FC, ReactNode } from 'react'

type DefaultSolutionViewSelectorProps = {
  onChooseSolution: (label: SolutionType) => void
  solutionPickerVersion: string
  title?: ReactNode
  description?: ReactNode
  onSelectionChange?: (solution: SolutionType | null) => void
}

const DefaultSolutionViewSelector: FC<DefaultSolutionViewSelectorProps> = ({
  onChooseSolution,
  solutionPickerVersion,
  title,
  description,
  onSelectionChange,
}) => {
  const solutionOptionsV4 = useSolutionOptions()
  const solutionOptionsV6 = useSolutionOptionsV6()

  const [selectedSolution, setSelectedSolution] = useState<SolutionType | null>(null)

  const handleCardInteraction = (label: SolutionType) => {
    if (solutionPickerVersion === 'v6') {
      setSelectedSolution(label)
      onSelectionChange?.(label)
    } else {
      onChooseSolution(label)
    }
  }

  if (solutionPickerVersion === 'v6') {
    return (
      <EuiFlexGroup
        direction='column'
        alignItems='flexStart'
        gutterSize='m'
        css={{ maxWidth: '600px' }}
      >
        {title && description && (
          <EuiFlexItem grow={false}>
            <TitleAndDescription title={title} description={description} isV6={true} />
          </EuiFlexItem>
        )}
        <EuiFlexItem grow={false}>
          <EuiFlexGroup direction='column' alignItems='flexStart' gutterSize='m'>
            {solutionOptionsV6.map((solutionOption) => (
              <EuiFlexItem key={solutionOption.label}>
                <SelectSolutionCardV6
                  {...solutionOption}
                  onNext={handleCardInteraction}
                  dataAttributePrefix='deployment-select-solution-'
                  checked={selectedSolution === solutionOption.label}
                />
              </EuiFlexItem>
            ))}
            <EuiFlexItem grow={false}>
              <EuiButton
                onClick={() => selectedSolution && onChooseSolution(selectedSolution)}
                fill={true}
                disabled={!selectedSolution}
              >
                <FormattedMessage id='select-solution.select-continue' defaultMessage='Continue' />
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    )
  }

  return (
    <EuiFlexGroup justifyContent='center'>
      {solutionOptionsV4.map((solutionOption) => (
        <EuiFlexItem key={solutionOption.label}>
          <SelectSolutionCardV4
            {...solutionOption}
            onNext={handleCardInteraction}
            dataAttributePrefix='deployment-select-solution-'
          />
        </EuiFlexItem>
      ))}
    </EuiFlexGroup>
  )
}

export default DefaultSolutionViewSelector
